import { ChangeEvent, Dispatch, FunctionComponent, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/system";

import { faClipboard, faCopy } from "@fortawesome/pro-regular-svg-icons";

import { Icon, Modal } from "@/components";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { useCreateApiKeyMutation } from "@/pages/Private/redux/apiKey/apiKey.api";
import {
	CreateApiKey,
	CreateApiKeySchema,
} from "@/pages/Private/pages/Company/pages/ApiKeyOverview/schema/apiKey";
import { InputField } from "@/components/InputField/InputField";
import { copyText } from "@/utils/copyText";

interface createApiKeyModalProps {
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const CreateApiKeyModal: FunctionComponent<createApiKeyModalProps> = ({
	isOpen,
	setIsOpen,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`apiKey.${key}`);
	const [createApiKey, { isLoading, error }] = useCreateApiKeyMutation();

	const [name, setName] = useState<string>("");
	const [apiKey, setApiKey] = useState<string>();
	const [isCopied, setIsCopied] = useState<boolean>(false);
	const handleStateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setValue(e.target.name as keyof CreateApiKey, e.target.value, {
			shouldDirty: true,
			shouldValidate: true,
		});

		setName(e.target.value);
	};

	const {
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm<CreateApiKey>({
		defaultValues: {
			name: "",
		},
		resolver: zodResolver(CreateApiKeySchema),
	});

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const onSubmit = async (values: CreateApiKey) => {
		try {
			const res = await createApiKey(values).unwrap();

			setApiKey(res.apiKey || "");
		} catch (err) {
			console.error(err);
		}
	};

	return !apiKey ? (
		<Modal
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			submitButtonText={t("basics.confirm")}
			title={ts("create.title")}
		>
			<Stack spacing={2}>
				<InputField
					error={!!errors.name?.message}
					handleChange={(e: ChangeEvent<HTMLInputElement>) => handleStateChange(e)}
					label={ts("name")}
					name={"name"}
					placeholder={ts("name")}
					value={name}
				/>

				{getAllErrors(error, formErrors).length
					? renderErrorMessages(getAllErrors(error, formErrors))
					: null}
			</Stack>
		</Modal>
	) : (
		<Modal
			handleSave={() => {
				setApiKey(undefined);
				setValue("name", "");
				setName("");
				setIsCopied(false);
				setIsOpen(false);
			}}
			isLoading={isLoading}
			isOpened={isOpen}
			submitButtonText={"Done"}
			title={"Your new API key"}
		>
			<div className="text-sm mb-2 font-bold text-text-meta">Keep your API key safe</div>
			<div className="text-sm mb-4 text-text-meta">
				Save and store this new key to a secure place, such as a password manager or secret store.
				You won&apos;t be able to see it again.
			</div>
			<div className="group relative mt-4">
				<div className="absolute top-0 text-sm -translate-y-[110%] left-1/2 -translate-x-1/2 group-hover:block hidden border border-border-light py-1 px-2 bg-text-meta text-white rounded-md">
					<Icon className="mr-2" icon={faClipboard} /> {isCopied ? "Copied" : "Copy to clipboard"}
				</div>
				<div
					className="flex flex-row justify-between cursor-pointer items-center rounded-md border border-border-light p-2"
					onClick={() => {
						void copyText(apiKey).then(setIsCopied);
					}}
				>
					<div className="text-sm grow text-center whitespace-nowrap">{apiKey}</div>
					<Icon className="text-gray-500 group-hover:text-gray-700" icon={faCopy} />
				</div>
			</div>
		</Modal>
	);
};
